import { GA4Head } from './GA4';
import { GTMHead } from './GTM';
import { HubspotHead } from './Hubspot';
import type { ClientConfig } from '@archipro-website/config';
import { useRootData } from '@modules/root';
import {
    hasSaleOrSharingOptOut,
    shouldAllowThirdPartySharing,
} from './allow-third-party-sharing';
import { isConfiguredPixelId } from './is-configured-pixel-id';

/**
 * Server-side rendered head pixels. Advertising pixels (Facebook, Pinterest,
 * LinkedIn, Outbrain) live in `ClientAdvertisingPixels`; the analytics trackers
 * here read the same shared third-party-sharing gate (GPC where the market
 * honours it, or an explicit sale/sharing opt-out from either the signed-in
 * member's preference or the opt-out cookie):
 *
 * - GA4 is dropped entirely while the signal is active.
 * - GTM keeps loading, with Google Consent Mode defaulted to denied, so
 *   first-party aggregate measurement survives while ad/remarketing sharing
 *   stops. Its noscript half is suppressed in `BodyPixels`, where Consent Mode
 *   cannot apply.
 * - Hubspot is not gated: it operates as ArchiPro's service provider under its
 *   data-processing agreement, with no ad-audience features enabled, so a
 *   sale/sharing opt-out does not cover it. If Hubspot ad or audience-sync
 *   features are ever switched on, that flow must join this gate.
 */
export const HeadPixels = ({
    gtmId,
    ga4TrackingId,
    hubspotTrackingId,
}: Pick<
    ClientConfig['pixels'],
    'gtmId' | 'ga4TrackingId' | 'hubspotTrackingId'
>) => {
    const { localisation, globalPrivacyControl, saleSharingOptOut, user } =
        useRootData();
    const allowSharing = shouldAllowThirdPartySharing(
        localisation.countryConfig.honourGlobalPrivacyControl,
        globalPrivacyControl,
        hasSaleOrSharingOptOut(user) || saleSharingOptOut,
    );

    return (
        <>
            {isConfiguredPixelId(gtmId) && (
                <GTMHead id={gtmId} denySharingConsent={!allowSharing} />
            )}
            {allowSharing && isConfiguredPixelId(ga4TrackingId) && (
                <GA4Head id={ga4TrackingId} />
            )}
            {isConfiguredPixelId(hubspotTrackingId) && (
                <HubspotHead hubspotTrackingId={hubspotTrackingId} />
            )}
        </>
    );
};
